import {
  FileFolderCategory,
  IncorporationRequestStatus,
  OwnershipRelationshipType,
  RecurringUserTaskStatus,
  RootFiIntegration,
  SecureInformationRequestFieldType,
  ServiceRequestStatus,
  StakeholderRelationshipType,
} from '@commenda/prisma';
import { LocationTypeRevamp, TSalesTaxFlow } from '@commenda/types';
import {
  BoardPositionEnum,
  BusinessPurposeEnum,
  EducationalQualificationEnum,
  JobTitleEnum,
  NonUSEntityTaxClassificationEntityTypeEnum,
  StructureTypes,
  TaxClassificationEntityTypeEnum,
} from '@commenda/types/src/random';
import { AccountingBasisEnum, AccountingFrequencyEnum } from './enums';

import { SalesTaxFilingFrequencyEnum } from '@prisma/client';

export enum Gender {
  Male = 'Male',
  Female = 'Female',
  Other = 'Other',
}

export const StakeholderRelationshipTypeMap: {
  [key in StakeholderRelationshipType]: string;
} = {
  ADVISOR: 'Advisor',
  BOARD_MEMBER: 'Board Member',
  CONSULTANT: 'Consultant',
  EMPLOYEE: 'Employee',
  EX_ADVISOR: 'Ex-Advisor',
  EX_CONSULTANT: 'Ex-Consultant',
  EX_EMPLOYEE: 'Ex-Employee',
  EXECUTIVE: 'Executive',
  FOUNDER: 'Founder',
  HOLDING_COMPANY: 'Holding Company',
  INVESTOR: 'Investor',
  NON_US_EMPLOYEE: 'Non US Employee',
  OFFICER: 'Officer',
  OTHER: 'Other',
  PARENT_COMPANY: 'Parent Company',
};

// Display labels for the ownership-feature relationship picker. Lives next
// to StakeholderRelationshipTypeMap so designers / engineers eyeballing
// either map see them side-by-side.
export const OwnershipRelationshipTypeMap: {
  [key in OwnershipRelationshipType]: string;
} = {
  FOUNDER: 'Founder',
  INVESTOR: 'Investor',
  EMPLOYEE: 'Employee',
  BOARD_MEMBER: 'Board member',
  ADVISOR: 'Advisor',
  PARENT: 'Parent',
  OTHER: 'Other',
};

// The two restricted lists the Add / Edit forms surface in the picker
// (per design). The Edit form merges these as a union when isEntity is
// determined dynamically.
export const OWNERSHIP_INDIVIDUAL_RELATIONSHIPS: OwnershipRelationshipType[] = [
  'FOUNDER',
  'INVESTOR',
  'EMPLOYEE',
  'BOARD_MEMBER',
  'ADVISOR',
  'OTHER',
];

export const OWNERSHIP_ENTITY_RELATIONSHIPS: OwnershipRelationshipType[] = [
  'PARENT',
  'INVESTOR',
  'OTHER',
];

export const ROOTFI_INTEGRATION_ENUM_TO_TEXT: {
  [key in RootFiIntegration]: string;
} = {
  [RootFiIntegration.QUICKBOOKS_SANDBOX]: 'QuickBooks Sandbox',
  [RootFiIntegration.ROOTFI_SANDBOX]: 'Rootfi Sandbox',
  [RootFiIntegration.MEKARI_JURNAL]: 'Mekari Jurnal',
  [RootFiIntegration.MS_DYNAMICS_365]: 'MS Dynamics 365',
  [RootFiIntegration.BUSY]: 'Busy',
  [RootFiIntegration.MYOB_BUSINESS]: 'Myob Business',
  [RootFiIntegration.NETSUITE]: 'NetSuite',
  [RootFiIntegration.ODOO_ACCOUNTING]: 'Odoo Accounting',
  [RootFiIntegration.QOYOD]: 'Qoyod',
  [RootFiIntegration.QUICKBOOKS]: 'Quickbooks',
  [RootFiIntegration.SAGE_CLOUD_ACCOUNTING]: 'Sage Cloud Accounting',
  [RootFiIntegration.SAGE_ZA_CLOUD_ACCOUNTING]: 'Sage ZA Cloud Accounting',
  [RootFiIntegration.TALLY]: 'Tally',
  [RootFiIntegration.WAFEQ]: 'Wafeq',
  [RootFiIntegration.FRESHBOOKS]: 'FreshBooks',
  [RootFiIntegration.WAVE]: 'Wave',
  [RootFiIntegration.XERO]: 'Xero',
  [RootFiIntegration.ZOHO_BOOKS]: 'Zoho Books',
  [RootFiIntegration.SHOPIFY]: 'Shopify',
  [RootFiIntegration.STRIPE]: 'Stripe',
  [RootFiIntegration.RAZORPAY]: 'RazorPay',
  [RootFiIntegration.PAYPAL]: 'PayPal',
  [RootFiIntegration.HUBSPOT]: 'HubSpot',
  [RootFiIntegration.ZOHO_CRM]: 'Zoho CRM',
  [RootFiIntegration.PIPEDRIVE]: 'Pipedrive',
  [RootFiIntegration.WOO_COMMERCE]: 'WooCommerce',
  [RootFiIntegration.BIG_COMMERCE]: 'BigCommerce',
  [RootFiIntegration.AMAZON]: 'Amazon',
  [RootFiIntegration.PUZZLE]: 'Puzzle',
  [RootFiIntegration.PUZZLE_SANDBOX]: 'Puzzle Sandbox',
  [RootFiIntegration.RILLET]: 'Rillet',
  [RootFiIntegration.RILLET_SANDBOX]: 'Rillet Sandbox',
  [RootFiIntegration.EPICOR_PROPHET_21]: 'Epicor Prophet 21',
};

export const WORK_REQUEST_STATUS_ENUM_TO_TEXT: {
  [key in IncorporationRequestStatus]: string;
} = {
  [IncorporationRequestStatus.Archive]: 'Archive',
  [IncorporationRequestStatus.Completed]: 'Completed',
  [IncorporationRequestStatus.CorrectionPending]: 'Correction Pending',
  [IncorporationRequestStatus.Filed]: 'Filed',
  [IncorporationRequestStatus.InProgress]: 'In Progress',
  [IncorporationRequestStatus.NotStarted]: 'Not Started',
  [IncorporationRequestStatus.Submitted]: 'Submitted',
};

export const SERVICE_REQUEST_STATUS_ENUM_TO_TEXT: {
  [key in ServiceRequestStatus]: string;
} = {
  [ServiceRequestStatus.COMPLETED]: 'Completed',
  [ServiceRequestStatus.NOT_STARTED]: 'Not Started',
  [ServiceRequestStatus.IN_PROGRESS]: 'In Progress',
  [ServiceRequestStatus.ARCHIVED]: 'Archived',
};

export enum OptionType {
  YES = 'YES',
  NO = 'NO',
  MAYBE = 'MAYBE',
}

export const STRUCTURE_ENUM_TO_TEXT: Record<StructureTypes, string> = {
  PARENT_COMPANY: 'Parent Company',
  STANDALONE: 'Standalone',
  SUBSIDIARY: 'Subsidiary',
  NOT_SURE: 'Not sure',
};

export const FILE_FOLDER_CATEGORY_ENUM_TO_TEXT: Record<
  FileFolderCategory,
  string
> = {
  [FileFolderCategory.ODI_DOCUMENTS]: 'ODI Documents',
  [FileFolderCategory.FDI_DOCUMENTS]: 'FDI Documents',
  [FileFolderCategory.SALES_TAX_DOCUMENTS]: 'Sales tax documents',
  [FileFolderCategory.OTHER]: 'Other',
};

export const EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT: {
  [key in EducationalQualificationEnum]: string;
} = {
  [EducationalQualificationEnum.BachelorsDegree]: `Bachelor's Degree`,
  [EducationalQualificationEnum.MastersDegree]: `Master's Degree`,
  [EducationalQualificationEnum.PrimaryEducation]: `Primary Education`,
  [EducationalQualificationEnum.SecondaryEducation]: `Secondary Education`,
  [EducationalQualificationEnum.VocationalEducation]: `Vocational Education`,
  [EducationalQualificationEnum.DoctrateOrHigher]: `Doctorate Or Higher`,
  [EducationalQualificationEnum.Professional]: `Professional`,
  [EducationalQualificationEnum.Diploma]: `Diploma`,
};

export const ACCOUNTING_BASIS_ENUM_TO_TEXT: {
  [key in AccountingBasisEnum]: string;
} = {
  [AccountingBasisEnum.CASH]: 'Cash',
  [AccountingBasisEnum.ACCRUAL]: 'Accrual',
};

export const ACCOUNTING_FREQUENCY_ENUM_TO_TEXT: {
  [key in AccountingFrequencyEnum]: string;
} = {
  [AccountingFrequencyEnum.MONTHLY]: 'Monthly',
  [AccountingFrequencyEnum.QUARTERLY]: 'Quarterly',
};

export const TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT: Record<
  TaxClassificationEntityTypeEnum,
  string
> = {
  [TaxClassificationEntityTypeEnum.C_CORPORATION]: 'C Corporation',
  [TaxClassificationEntityTypeEnum.S_CORPORATION]: 'S Corporation',
  [TaxClassificationEntityTypeEnum.PARTNERSHIP]: 'Partnership',
  [TaxClassificationEntityTypeEnum.INDIVIDUAL_OR_SOLE_PROPRIETORSHIP]:
    'Individual/Sole Proprietorship',
  [TaxClassificationEntityTypeEnum.DISREGARDED_ENTITY]: 'Disregarded',
  [TaxClassificationEntityTypeEnum.FOREIGN_CORPORATION]: 'Foreign Corporation',
  [TaxClassificationEntityTypeEnum.FOREIGN_PARTNERSHIP]: 'Foreign Partnership',
};

export const NON_US_TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT: {
  [key in NonUSEntityTaxClassificationEntityTypeEnum]: string;
} = {
  [NonUSEntityTaxClassificationEntityTypeEnum.FOREIGN_CORPORATION]:
    'Foreign Corporation',
  [NonUSEntityTaxClassificationEntityTypeEnum.FOREIGN_PARTNERSHIP]:
    'Foreign Partnership',
};

export const SECURE_INFORMATION_REQUEST_FIELD_TYPE_TO_TEXT: {
  [key in SecureInformationRequestFieldType]: string;
} = {
  [SecureInformationRequestFieldType.TEXT]: 'Text',
  [SecureInformationRequestFieldType.NUMBER]: 'Number',
  [SecureInformationRequestFieldType.DATE]: 'Date',
  [SecureInformationRequestFieldType.BIG_TEXT]: 'Long Text',
  [SecureInformationRequestFieldType.RADIO]: 'Yes/No',
  [SecureInformationRequestFieldType.SELECT]: 'Select',
  [SecureInformationRequestFieldType.MULTI_SELECT]: 'Multi Select',
  [SecureInformationRequestFieldType.ADDRESS]: 'Address',
  [SecureInformationRequestFieldType.FILE]: 'File',
  [SecureInformationRequestFieldType.MULTI_FILE]: 'Multi File',
  [SecureInformationRequestFieldType.PHONE_NUMBER]: 'Phone Number',
};

export const RECURRING_USER_TASK_STATUS_ENUM_TO_TEXT: {
  [key in RecurringUserTaskStatus]: string;
} = {
  [RecurringUserTaskStatus.NOT_STARTED]: 'Not started',
  [RecurringUserTaskStatus.INPROGRESS]: 'In progress',
  [RecurringUserTaskStatus.COMPLETED]: 'Completed',
  [RecurringUserTaskStatus.CANCELLED]: 'Cancelled',
};

export const BUSINESS_PURPOSE_ENUM_TO_TEXT: {
  [key in BusinessPurposeEnum]: string;
} = {
  [BusinessPurposeEnum.ACCOMMODATIONS]: 'Accommodations',
  [BusinessPurposeEnum.CONSTRUCTION]: 'Construction',
  [BusinessPurposeEnum.FINANCE]: 'Finance',
  [BusinessPurposeEnum.FOOD_SERVICE]: 'Food Service',
  [BusinessPurposeEnum.HEALTH_CARE]: 'Health Care',
  [BusinessPurposeEnum.INSURANCE]: 'Insurance',
  [BusinessPurposeEnum.MANUFACTURING]: 'Manufacturing',
  [BusinessPurposeEnum.REAL_ESTATE]: 'Real Estate',
  [BusinessPurposeEnum.RENTAL_LEASING]: 'Rental & Leasing',
  [BusinessPurposeEnum.RETAIL]: 'Retail',
  [BusinessPurposeEnum.SOCIAL_ASSISTANCE]: 'Social Assistance',
  [BusinessPurposeEnum.TRANSPORTATION]: 'Transportation',
  [BusinessPurposeEnum.WAREHOUSING]: 'Warehousing',
  [BusinessPurposeEnum.WHOLESALE]: 'Wholesale',
  [BusinessPurposeEnum.CONSULTING]: 'Consulting',
  [BusinessPurposeEnum.ORGANIZATION]: 'Organization',
  [BusinessPurposeEnum.RENTAL]: 'Rental',
  [BusinessPurposeEnum.REPAIR]: 'Repair',
  [BusinessPurposeEnum.SELL_GOODS]: 'Sell goods',
  [BusinessPurposeEnum.SERVICE]: 'Service',
  [BusinessPurposeEnum.SOFTWARE_SERVICES]: 'Software Services',
  [BusinessPurposeEnum.OTHER]: 'Other',
};

export const BOARD_POSITION_ENUM_TO_TEXT: {
  [key in BoardPositionEnum]: string;
} = {
  [BoardPositionEnum.President]: 'President',
  [BoardPositionEnum.Secretary]: 'Secretary',
  [BoardPositionEnum.Treasurer]: 'Treasurer',
  [BoardPositionEnum.Manager]: 'Manager',
  [BoardPositionEnum.BankSignatory]: 'Bank Signatory',
};

export const JOB_TITLE_ENUM_TO_TEXT: {
  [key in JobTitleEnum]: string;
} = {
  [JobTitleEnum.ChiefExecutiveOfficer]: 'Chief Executive Officer',
  [JobTitleEnum.ChiefTechnologyOfficer]: 'Chief Technology Officer',
  [JobTitleEnum.ChiefFinancialOfficer]: 'Chief Financial Officer',
  [JobTitleEnum.ChiefOperatingOfficer]: 'Chief Operating Officer',
  [JobTitleEnum.ChiefMarketingOfficer]: 'Chief Marketing Officer',
  [JobTitleEnum.ChiefInformationOfficer]: 'Chief Information Officer',
  [JobTitleEnum.DirectorOfComplaince]: 'Director of Compliance',
  [JobTitleEnum.DirectorOfOperations]: 'Director of Operations',
  [JobTitleEnum.HeadOfTax]: 'Head of Tax',
  [JobTitleEnum.Others]: 'Other',
};

export const PAYMENT_INTERVAL_ENUM_TO_TEXT: {
  [key in SalesTaxFilingFrequencyEnum]: string;
} = {
  [SalesTaxFilingFrequencyEnum.ANNUAL_CALENDAR_YEAR]: 'Annual Calendar Year',
  [SalesTaxFilingFrequencyEnum.MONTHLY]: 'Monthly',
  [SalesTaxFilingFrequencyEnum.QUARTERLY]: 'Quarterly',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_PREPAY_MONTHLY]:
    'Quarterly Prepay Monthly',
  [SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY]: 'Semi Anually',
  [SalesTaxFilingFrequencyEnum.FISCAL_YEAR]: 'Fiscal Year',
  [SalesTaxFilingFrequencyEnum.MONTHLY_ACCELERATED_PREPAY_EARLY]:
    'Monthly Accelerated Prepay Early',
  [SalesTaxFilingFrequencyEnum.ANNUAL_RECONCILIATION]: 'Annual Reconciliation',
  [SalesTaxFilingFrequencyEnum.BI_MONTHLY]: 'Bi-Monthly',
  [SalesTaxFilingFrequencyEnum.TRI_ANNUALLY]: 'Tri-Annually',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_FEB]: 'Quarterly (Feb start)',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_MAR]: 'Quarterly (Mar start)',
};

// String-based frequency map for V2 frequencies not yet in the generated Prisma
// enum. The frontend's @prisma/client lags the backend schema, so the enum
// members below are `undefined` at runtime — meaning the computed keys in
// PAYMENT_INTERVAL_ENUM_TO_TEXT collapse to a single "undefined" key and never
// match. Re-declaring them here with literal string keys keeps the labels
// working regardless of the generated client. Backend is the source of truth.
export const FREQUENCY_DISPLAY_NAMES: Record<string, string> = {
  ...PAYMENT_INTERVAL_ENUM_TO_TEXT,
  ANNUAL_RECONCILIATION: 'Annual Reconciliation',
};

export const SALES_TAX_FLOW_TO_TEXT: Record<TSalesTaxFlow, string> = {
  SALES_TAX_REGISTRATION: 'Sales Tax Registration',
  SALES_TAX_FILING: 'Indirect Tax Filing',
};

export const LOCATION_TYPE_TO_TEXT: Record<LocationTypeRevamp, string> = {
  // Address-role types
  [LocationTypeRevamp.REGISTERED_OFFICE]: 'Registered office',
  [LocationTypeRevamp.MAILING_ADDRESS]: 'Mailing address',

  // Physical-presence types
  [LocationTypeRevamp.PRINCIPAL_PLACE_OF_BUSINESS]:
    'Headquarters / Operating address',
  [LocationTypeRevamp.BRANCH_OFFICE]: 'Branch office',
  [LocationTypeRevamp.RETAIL_LOCATION]: 'Retail location',
  [LocationTypeRevamp.FACTORY]: 'Factory',
  [LocationTypeRevamp.WAREHOUSE]: 'Warehouse',
  [LocationTypeRevamp.NATURAL_RESOURCE_SITE]: 'Natural resource site',
  [LocationTypeRevamp.REPRESENTATIVE_OFFICE]: 'Representative office',
  [LocationTypeRevamp.CONSTRUCTION_SITE]: 'Construction site',
  [LocationTypeRevamp.THIRD_PARTY_FULFILLMENT]: 'Third-party fulfillment',
  [LocationTypeRevamp.REMOTE_EMPLOYEE]: 'Remote employee',
  [LocationTypeRevamp.DEPENDENT_AGENT]: 'Dependent agent',
  [LocationTypeRevamp.INDEPENDENT_AGENT]: 'Independent agent',

  // Catch-all + EU-specific
  [LocationTypeRevamp.OTHER]: 'Other',
  [LocationTypeRevamp.REGISTRATION]: 'Tax registration',
};
